// We expect k4model to be defined globally...
/* global k4 */

import _ from "lodash";

import { set } from "@/utils/vuex";
import * as k4util from "@/utils/k4util";

import ModelManager from "@/services/k4/manager";

import wizards from "./wizards";

const getInitState = () => ({
	selectedSceneId: null,

	scenes: [],
	triggers: [],
	actions: [],
});

const getPropertyPath = (action, type) => _.get(action.properties[type], "value");

export default {
	namespaced: true,
	modules: {
		wizards,
	},
	state: getInitState(),

	getters: {
		findActionsForScene: state => scene => {
			const scenePath = scene.path;
			return _.filter(state.actions, action => getPropertyPath(action, "scene") === scenePath);
		},

		findTriggerForAction: state => action => {
			const path = getPropertyPath(action, "trigger");
			return _.find(state.triggers, { path });
		},

		selectedSceneId(state) {
			return state.selectedSceneId;
		},

		selectedScene(state, getters) {
			return _.find(state.scenes, ["guid", getters.selectedSceneId]);
		},

		selectedActions(state, getters) {
			const scenePath = _.get(getters.selectedScene, "path");
			const actions = _.filter(state.actions, action => getPropertyPath(action, "scene") === scenePath);

			return actions;
		},

		selectedTriggers(_state, getters) {
			const actions = getters.selectedActions;
			const triggers = _.chain(actions)
				.map(getters.findTriggerForAction)
				.compact()
				.value();
			return triggers;
		},

		scenes(state) {
			return state.scenes;
		},
	},

	mutations: {
		resetState(state) {
			Object.assign(state, getInitState());
		},

		setSelectedSceneId: set("selectedSceneId"),

		setScenes: set("scenes"),
		removeScene(state, scene) {
			state.scenes = _.reject(state.scenes, item => item.guid === scene.guid);
			state.selectedSceneId = null;
		},

		setTriggers: set("triggers"),
		setActions: set("actions"),
	},

	actions: {
		modelChanged: {
			root: true,
			async handler({ commit, rootState }) {
				const k4model = _.get(rootState, "controller.model.model", null);

				if (_.isNil(k4model)) {
					// Connection reset, we've disconnected from controller
					commit("resetState");
					return;
				}

				const manager = new ModelManager(k4model);
				const model = await manager.render();

				window.k4model = k4model;
				window.model = model;

				commit("setScenes", manager.scenes);
				commit("setTriggers", manager.triggers);
				commit("setActions", manager.actions);
			},
		},

		async addScene({ dispatch, rootGetters }, { name }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			// Trim all whitespace surrounding name
			name = _.trim(name);

			window.vm.$log(`Adding new scene named '${name}'`);
			await dispatch(
				"controller/nodes/add",
				{
					name,
					subpath: "Scenes",
					description: findByClass(k4util.DEVICE_CLASS_SCENE),
				},
				{ root: true }
			);
			const sceneNode = k4.model.child(`Scenes/${name}`);

			window.vm.$log(`Adding new trigger named '${name}'`);
			await dispatch(
				"controller/nodes/add",
				{
					name,
					subpath: "Triggers",
					description: findByClass(k4util.DEVICE_CLASS_TRIGGER),
				},
				{ root: true }
			);
			const triggerNode = k4.model.child(`Triggers/${name}`);

			window.vm.$log(`Adding new action named '${name}'`);
			await dispatch(
				"controller/nodes/add",
				{
					name,
					subpath: "Actions",
					description: findByClass(k4util.DEVICE_CLASS_ACTION),
				},
				{ root: true }
			);
			const actionNode = k4.model.child(`Actions/${name}`);

			// Update action node with scene and trigger paths
			window.vm.$log(`Linking trigger to action: ${triggerNode.path()}`);
			actionNode.child("properties/trigger").set(triggerNode.path());

			window.vm.$log(`Linking scene to action: ${sceneNode.path()}`);
			actionNode.child("properties/scene").set(sceneNode.path());

			return sceneNode;
		},

		// Renames all nodes that:
		//  * Are connected by association to the selected scene
		//  * Have the same name as the selected scene
		// But only if renaming said nodes would not result in a naming conflict
		async renameScene({ dispatch, getters, rootGetters }, { name }) {
			const selectedScene = getters["selectedScene"];

			// Trim all whitepace surrounding name
			name = _.trim(name);

			// The scene is already the desired name.
			if (selectedScene.name === name) return;

			window.vm.$log(
				`Renaming scene from '${selectedScene.name}' to '${name}'`
			);

			// Check for potential naming conflicts.
			_.forEach(
				{
					scene: k4util.DEVICE_CLASS_SCENE,
					action: k4util.DEVICE_CLASS_ACTION,
					trigger: k4util.DEVICE_CLASS_TRIGGER,
				},
				(cls, clsName) => {
					const nodes = k4.model.classes[cls];
					const node = _.find(nodes, node => node.name() === name);
					if (!_.isNil(node))
						throw new Error(
							`Name conflicts with an existing ${clsName}, at '${node.path()}'.`
						);
				}
			);

			// Find triggers and actions conected to current scene that also have the same name.
			const renameNodes = _(
				[selectedScene].concat(
					getters["selectedActions"],
					getters["selectedTriggers"]
				)
			)
				.filter(node => node.name === selectedScene.name)
				.map(node => node.path)
				.map(rootGetters["controller/nodes/nodeByPath"])
				.compact()
				.value();

			for (const node of renameNodes)
				await dispatch(
					"controller/nodes/rename",
					{ node, name },
					{ root: true }
				);
		},

		async removeScene({ commit, dispatch, state, getters, rootGetters }) {
			const markedScene = getters["selectedScene"];
			const markedActions = getters["selectedActions"];

			let markedTriggers = getters["selectedTriggers"];
			{
				const markedActionGuids = _.map(markedActions, "guid");
				const unmarkedActions = _.reject(state.actions, action =>
					_.includes(markedActionGuids, action.guid)
				);
				markedTriggers = _.reject(markedTriggers, trigger => {
					// Check if trigger also belongs to a *un*marked action
					return _.find(
						unmarkedActions,
						action => action.triggerPath === trigger.path
					);
				});
			}

			window.vm.$log(
				`Deleting scene '${markedScene.name}' along with ${markedActions.length} associated actions and ${markedTriggers.length} triggers`
			);

			const markedNodes = [markedScene].concat(markedActions, markedTriggers);
			markedNodes.unshift(..._.flatMap(markedNodes, "children"));

			const removePromise = markedNodes.map(
				node => dispatch("controller/nodes/remove", node, { root: true })
			);
			await Promise.all(removePromise);

			// For some reason it takes the model a *very* long time to update. Go
			// ahead and perform a manual optimistic update on state now.
			commit("removeScene", markedScene);
		},

		async addCondition({ dispatch, rootGetters }, { parent, condition }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			window.vm.$log(
				`Adding new condition: ${JSON.stringify(condition)} to parent ${parent.path
				}`
			);

			const name = `Condition ${Date.now()}`;
			const description = findByClass(k4util.DEVICE_CLASS_CONDITION);
			description.properties.path = condition.path;
			description.properties.operator = condition.operator;
			description.properties.value = condition.value;
			await dispatch(
				"controller/nodes/add",
				{
					name,
					description,
					subpath: parent.path,
				},
				{ root: true }
			);
		},

		async updateCondition({ dispatch, rootGetters }, { node, condition }) {
			window.vm.$log(
				`Updating condition: ${JSON.stringify(condition)} at ${node.path}`
			);

			await dispatch(
				"controller/nodes/setProperties",
				{
					nodeGuid: node.guid,
					properties: {
						path: condition.path,
						operator: condition.operator,
						value: condition.value,
					},
				},
				{ root: true }
			);
		},

		addInterval({ dispatch, rootGetters }, { parent, interval }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			window.vm.$log(
				`Adding new interval condition: ${JSON.stringify(interval)} to parent ${parent.path
				}`
			);

			const name = `Interval ${Date.now()}`;
			const description = findByClass(k4util.DEVICE_CLASS_INTERVAL);
			description.properties.seconds = interval.seconds;

			return dispatch(
				"controller/nodes/add",
				{ name, description, subpath: parent.path },
				{ root: true }
			);
		},

		updateInterval({ dispatch }, { node, interval }) {
			window.vm.$log(
				`Updating interval: ${JSON.stringify(interval)} at ${node.path}`
			);

			return dispatch(
				"controller/nodes/setProperties",
				{
					nodeGuid: node.guid,
					properties: {
						seconds: interval.seconds,
					},
				},
				{ root: true }
			);
		},

		addOrBlock({ dispatch, rootGetters }, { trigger, when = false }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			window.vm.$log(`Adding new OR block to parent ${trigger.path}`);

			const name = `Or ${when ? "when " : ""}${Date.now()}`;
			const description = findByClass(k4util.DEVICE_CLASS_OR);

			return dispatch(
				"controller/nodes/add",
				{ name, description, subpath: trigger.path },
				{ root: true }
			);
		},

		// For adding either command or event steps. They use the same arguments and have the same class.
		addStep({ dispatch, rootGetters }, { scene, step }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			window.vm.$log(
				`Adding new command step: ${JSON.stringify(step)} to parent ${scene.path
				}`
			);

			const name = `Step ${Date.now()}`;
			const description = findByClass(k4util.DEVICE_CLASS_STEP);
			description.properties.path = step.path;
			description.properties.args = step.args;
			description.properties.order = step.order;

			return dispatch(
				"controller/nodes/add",
				{
					name,
					description,
					subpath: scene.path,
				},
				{ root: true }
			);
		},

		updateStep({ dispatch, rootGetters }, { node, step }) {
			window.vm.$log(`Updating step: ${JSON.stringify(step)} at ${node.path}`);

			return dispatch(
				"controller/nodes/setProperties",
				{
					nodeGuid: node.guid,
					properties: {
						order: step.order,
						path: step.path,
						args: step.args,
					},
				},
				{ root: true }
			);
		},

		addDelayStep({ dispatch, rootGetters }, { scene, step }) {
			const findByClass = cls =>
				rootGetters["descriptions/findByClass"](cls).data;

			window.vm.$log(
				`Adding new delay step: ${JSON.stringify(step)} to parent ${scene.path}`
			);

			const name = `Delay ${Date.now()}`;
			const description = findByClass(k4util.DEVICE_CLASS_DELAY);
			description.properties.seconds = step.seconds;

			return dispatch(
				"controller/nodes/add",
				{
					name,
					description,
					subpath: scene.path,
				},
				{ root: true }
			);
		},

		updateDelayStep({ dispatch }, { node, step }) {
			window.vm.$log(
				`Updating delay step: ${JSON.stringify(step)} at ${node.path}`
			);

			return dispatch(
				"controller/nodes/setProperties",
				{
					nodeGuid: node.guid,
					properties: {
						order: step.order,
						seconds: step.seconds,
					},
				},
				{ root: true }
			);
		},

		reorderSteps({ dispatch }, { scene, node, move }) {
			let steps = _.map(scene.steps, "guid");
			const curIdx = steps.findIndex(guid => guid === node.guid);

			delete steps[curIdx];
			steps = _.compact(steps);

			switch (move) {
				case "top":
					steps.unshift(node.guid);
					break;
				case "up":
					let targetIdx = curIdx - 1 >= 0 ? curIdx - 1 : 0;
					steps.splice(targetIdx, 0, node.guid);
					break;
				case "down":
					steps.splice(curIdx + 1, 0, node.guid);
					break;
				case "bottom":
					steps.push(node.guid);
					break;
			}

			return Promise.all(
				_.map(steps, (step, idx) => {
					dispatch(
						"controller/nodes/setProperties",
						{
							nodeGuid: step,
							properties: {
								// +1 to make indices start at 1
								order: idx + 1,
							},
						},
						{ root: true }
					);
				})
			);
		},
	},
};
